import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, ReplaySubject, switchMap, tap } from 'rxjs';
import { UserLogin } from './user.types';
import { BaseRespone } from '../models/common.response';
import { environment } from 'src/environments/environment';
import { IHospitalInfo } from '../pages/hospital/ihospital-info';
import { IUsers } from '../pages/user/iusers';

@Injectable({ providedIn: 'root' })
export class UserAuthService {
    private _httpClient = inject(HttpClient);
    private _user: ReplaySubject<IUsers | null> = new ReplaySubject<IUsers | null>(1);
    private _hospital: ReplaySubject<UserLogin | null> = new ReplaySubject<UserLogin | null>(1);
    private userData = new BehaviorSubject<any>(null);
    userData$ = this.userData.asObservable();

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set user(value: IUsers | null) {
        // Store the value
        this._user.next(value);
    }

    get user$(): Observable<IUsers | null> {
        return this._user.asObservable();
    }

    setUserData(data: any) {
        this.userData.next(data);
    }

    getUserData() {
        return this.userData.getValue();
    }

    clearSession(): void {
        this._user.next(null);
        this._hospital.next(null);
        this.userData.next(null);
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the current signed-in user data
     */
    get(id: number): Observable<BaseRespone<IUsers>> {
        return this._httpClient.get<BaseRespone<IUsers>>(`${environment.baseURl}/User/${id}`).pipe(
            tap((response) => {
                this._user.next(response.result);
            }),
            catchError((error) => {
                console.error('Error fetching user data:', error);
                throw error;
            })
        );
    }

    getHospital(hospitalID: number): Observable<BaseRespone<IHospitalInfo>> {
        return this._httpClient.get<BaseRespone<any>>(`${environment.baseURl}/Hospital/${hospitalID}`).pipe(
            tap((response) => {
                this._hospital.next(response.result);
            }),
            catchError((error) => {
                console.error('Error fetching user data:', error);
                throw error;
            })
        );
    }

    /**
     * Update the user
     *
     * @param user
     */
    update(user: IUsers): Observable<any> {
        return this._httpClient.patch<IUsers>('api/common/user', { user }).pipe(
            map((response) => {
                this._user.next(response);
            })
        );
    }

    /**
* Change user password
*
* @param credentials Object containing current and new password
*/
    changePassword(credentials: { currentPassword: string; newPassword: string }): Observable<BaseRespone<boolean>> {
        return this._httpClient.post<BaseRespone<boolean>>(`${environment.baseURl}/Auth/change-password`, credentials).pipe(
            switchMap((response) => {
                return of(response);
            })
        );
    }

    forgotPassword(credentials: { email: string }): Observable<any> {
        return this._httpClient.post(`${environment.baseURl}/Auth/forgot-password`, credentials).pipe(
            switchMap((response) => {
                return of(response);
            })
        );
    }

    resetPassword(payload: { email: string; token: string; newPassword: string }): Observable<BaseRespone<boolean>> {
        return this._httpClient.post<BaseRespone<boolean>>(`${environment.baseURl}/Auth/reset-password`, payload).pipe(
            switchMap((response) => {
                return of(response);
            })
        );
    }
}
